import type { DateTime } from "luxon";
import { deriveNurseStatusAtDate, getNurseStatusEndDate } from "../formatters";
import { EffectiveEmployeeStatus, type Nurse } from "../models";

/** Pre-add warning shown when an admin manually adds an On Leave / Suspended nurse to a shift. */
export interface EmployeeStatusWarningProps {
	status: "OnLeave" | "Suspended";
	/** Name parts, not a composed name: the dialog formats them with the department's name-order flag. */
	person: { firstName: string; lastName: string };
	/** Window end (already formatted for display), or null when open-ended. */
	endDate: string | null;
}

/**
 * Builds the On Leave / Suspended warning payload for a nurse being added to a shift on `atDate`,
 * or undefined when they are schedulable on that date. Derives the status at the shift's date (not
 * today) so adding into a future leave window still warns. Kept out of WarningDialog.tsx (like
 * `isAgencyNurse`) so pure warning logic can use it without pulling the dialog's contexts in.
 */
export const buildEmployeeStatusWarning = (nurse: Nurse, departmentId: string, atDate: DateTime): EmployeeStatusWarningProps | undefined => {
	const status = deriveNurseStatusAtDate(nurse, departmentId, atDate);
	if (status !== EffectiveEmployeeStatus.OnLeave && status !== EffectiveEmployeeStatus.Suspended) {
		return undefined;
	}
	const endDate = getNurseStatusEndDate(nurse, departmentId);
	return {
		status: status === EffectiveEmployeeStatus.Suspended ? "Suspended" : "OnLeave",
		person: { firstName: nurse.firstName, lastName: nurse.lastName },
		endDate: endDate ? endDate.toFormat("MMM d, yyyy") : null,
	};
};
